import type {
  CableGap,
  CableViewBundle,
  FindCableGaps,
  GapTranslate,
  SldCircuitRow,
  SldCircuits,
  SldIdColumn,
} from "./types";

// Same-name markers are either ~2 m apart (one panel room) or tens of metres
// apart across every project measured — nothing between, so this is not delicate.
const MERGED_BOARD_MIN_SPREAD_M = 3;

const MERGED_BOARD_BAD_SPREAD_M = 10;

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

const KEY = 'smartSurveyor.cablesBeta.gaps';

const planName = (bundle: CableViewBundle): string => bundle.plan_name ?? '?';

const distance = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

// The schematic writes "-2F1" where the plan tags "2F1".
const normalizeCircuit = (value: string | null | undefined): string =>
  (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * A board the run could not put on a drawn symbol. `placed_by` says the same
 * thing in prose, but the view bundle is published in one language only — so
 * the wording comes from `origin`, which is a code, not a sentence.
 */
const sourcesNotFound = (
  bundle: CableViewBundle,
  t: GapTranslate,
): CableGap[] => {
  const gaps: CableGap[] = [];

  for (const source of bundle.provenance?.sources ?? []) {
    const name = source.name ?? '?';
    if (source.estimated === true) {
      gaps.push({
        kind: 'source_position_guessed',
        severity: 'high',
        plan: planName(bundle),
        summary: t(`${KEY}.sourceGuessedSummary`, { name }),
        evidence: t(`${KEY}.sourceGuessedEvidence`),
        ask: {
          kind: 'point',
          prompt: t(`${KEY}.sourceGuessedPrompt`, { name }),
          source: name,
        },
        recalc: 'reroute_source',
      });
      continue;
    }
    // Label text puts the board in the right room but not on the right symbol,
    // so the lengths are close rather than measured. Worth confirming, not alarming.
    if (source.origin === 'plan_text') {
      gaps.push({
        kind: 'source_located_by_text',
        severity: 'medium',
        plan: planName(bundle),
        summary: t(`${KEY}.sourceByTextSummary`, { name }),
        evidence: t(`${KEY}.sourceByTextEvidence`),
        ask: {
          kind: 'point',
          prompt: t(`${KEY}.sourceByTextPrompt`, { name }),
          source: name,
        },
        recalc: 'reroute_source',
      });
    }
  }

  return gaps;
};

const mergedBoards = (
  bundle: CableViewBundle,
  t: GapTranslate,
): CableGap[] => {
  const scale = bundle.scale ?? 0;
  // Spread is only meaningful in metres; pixels alone would flag every plan.
  if (!scale) return [];

  const positionsByName = new Map<string, { x: number; y: number }[]>();
  for (const marker of bundle.board_markers ?? []) {
    if (marker.x === undefined || marker.y === undefined) continue;
    const name = marker.name ?? '';
    const existing = positionsByName.get(name) ?? [];
    existing.push({ x: marker.x, y: marker.y });
    positionsByName.set(name, existing);
  }

  const gaps: CableGap[] = [];
  for (const [name, positions] of positionsByName) {
    if (positions.length < 2) continue;
    let widest = 0;
    for (const a of positions) {
      for (const b of positions) widest = Math.max(widest, distance(a, b));
    }
    const spreadMetres = widest / scale;
    if (spreadMetres < MERGED_BOARD_MIN_SPREAD_M) continue;
    gaps.push({
      kind: 'boards_share_a_name',
      severity: spreadMetres >= MERGED_BOARD_BAD_SPREAD_M ? 'high' : 'medium',
      plan: planName(bundle),
      summary: t(`${KEY}.mergedBoardsSummary`, {
        count: positions.length,
        name,
      }),
      evidence: t(`${KEY}.mergedBoardsEvidence`, {
        spread: Math.round(spreadMetres),
      }),
      ask: {
        kind: 'name_each',
        prompt: t(`${KEY}.mergedBoardsPrompt`, { count: positions.length }),
        positions,
      },
      recalc: 'regroup_and_reroute',
    });
  }
  return gaps;
};

const unassignedDevices = (
  bundle: CableViewBundle,
  t: GapTranslate,
): CableGap[] => {
  const coverage = bundle.cached_batch?.summary?.estimation?.coverage;
  const devices = coverage?.devices ?? 0;
  const withCircuit = coverage?.with_circuit;
  if (!devices || withCircuit === undefined || withCircuit >= devices) return [];
  return [
    {
      kind: 'devices_unassigned',
      severity: 'medium',
      plan: planName(bundle),
      summary: t(`${KEY}.devicesUnassignedSummary`, {
        missing: devices - withCircuit,
        devices,
      }),
      evidence: t(`${KEY}.devicesUnassignedEvidence`, {
        withCircuit,
        devices,
      }),
      ask: { kind: 'none', prompt: '' },
      recalc: 'none',
    },
  ];
};

const circuitsNeverBuilt = (
  bundles: CableViewBundle[],
  sldCircuits: SldCircuits | null,
  t: GapTranslate,
): CableGap[] => {
  const built = new Set<string>();
  for (const bundle of bundles) {
    for (const circuit of bundle.cached_batch?.circuits ?? []) {
      const circuitId = circuit.circuit_id ?? '';
      if (circuitId.includes('·')) {
        built.add(normalizeCircuit(circuitId.split('·')[1]));
      }
    }
  }

  // Which column carries the number a plan prints varies by project: 153
  // tags devices A43, which is the schematic's `nr`, while 91 tags 2F4, which
  // is its `breaker`. Take whichever column the takeoff actually matches.
  const columns: SldIdColumn[] = ['breaker', 'nr', 'cable_id'];
  let listed = new Map<string, string>();
  let bestOverlap = -1;
  for (const column of columns) {
    const candidate = new Map<string, string>();
    for (const rows of Object.values(sldCircuits?.boards ?? {})) {
      for (const row of rows) {
        const key = normalizeCircuit((row as SldCircuitRow)[column] ?? null);
        const description = row.description ?? '';
        if (key && !description.toLowerCase().includes('reserve')) {
          candidate.set(key, description);
        }
      }
    }
    const overlapping = [...candidate.keys()].filter((key) =>
      built.has(key),
    ).length;
    if (overlapping > bestOverlap) {
      bestOverlap = overlapping;
      listed = candidate;
    }
  }

  if (!built.size || !listed.size) return [];

  // Zero overlap does not mean every circuit is missing — it means the two
  // documents do not number circuits the same way, so no cable can be traced
  // to a schematic circuit at all. That is worth saying outright rather than
  // reporting all of them as absent.
  const overlap = [...listed.keys()].filter((key) => built.has(key)).length;
  if (!overlap) {
    // The schematic knows its breakers; the plan is the side with nothing to
    // join on. Show several so it does not rest on one odd row (the first is
    // often the incomer, not an outgoing way).
    const schematicSample = [...listed.keys()].slice(0, 3).join(', ');
    return [
      {
        kind: 'takeoff_not_linked_to_schematic',
        severity: 'high',
        plan: '',
        summary: t(`${KEY}.notLinkedSummary`, { count: listed.size }),
        evidence: t(`${KEY}.notLinkedEvidence`, { sample: schematicSample }),
        ask: { kind: 'none', prompt: '' },
        recalc: 'none',
      },
    ];
  }

  const missing = [...listed.keys()].filter((key) => !built.has(key)).sort();
  if (!missing.length) return [];

  const shown = missing
    .slice(0, 6)
    .map((key) => `${key} (${(listed.get(key) ?? '').slice(0, 36)})`)
    .join(', ');
  return [
    {
      kind: 'circuit_not_on_any_plan',
      severity: 'medium',
      plan: '',
      summary: t(`${KEY}.circuitsMissingSummary`, {
        missing: missing.length,
        listed: listed.size,
      }),
      evidence: shown + (missing.length > 6 ? ' …' : ''),
      ask: { kind: 'none', prompt: '' },
      recalc: 'none',
    },
  ];
};

export const findCableGaps: FindCableGaps = ({ bundles, sldCircuits, t }) => {
  const gaps = bundles.flatMap((bundle) => [
    ...sourcesNotFound(bundle, t),
    ...mergedBoards(bundle, t),
    ...unassignedDevices(bundle, t),
  ]);
  gaps.push(...circuitsNeverBuilt(bundles, sldCircuits, t));

  return gaps.sort((a, b) => {
    const bySeverity = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    if (bySeverity !== 0) return bySeverity;
    if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
    return a.plan.localeCompare(b.plan);
  });
};
